<template>
  <AppResizableContainer layout="vertical">
    <template #headerContent>
      <div v-if="data" class="relative h-full pb-4 bg-white">
        <BaseSearchDropdown
          class="relative z-50 px-4 py-2"
          :class="{ 'hidden': mode == 'view' }"
          @updateSearch="updateSearch"
          @itemSelected="handleTraderSiteSelection"
          v-model="selectedTraderSite"
          :listItems="filteredTraderSites"
          :count="traderSitesCount"
          :noResultsText="$t('messages.noResultsText')" />
        <table class="relative z-0 w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
          <tbody>
            <BaseDetailsTableRow
              :name="$t('customer.traderSiteName')"
              mode="view"
              :isHorizontalLayout="isHorizontalLayout">
              <BaseInput
                v-if="data.defaultTraderSite"
                mode="view"
                v-model="data.defaultTraderSite.name"
                color="base"
                flat
                :disabled="true" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow
              mode="view"
              :name="$t('customer.traderSiteCode')"
              :isHorizontalLayout="isHorizontalLayout">
              <BaseInput
                v-if="data.defaultTraderSite"
                mode="view"
                v-model="data.defaultTraderSite.code"
                color="base"
                flat
                :disabled="true" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow
              mode="view"
              :name="$t('customer.traderSiteCity')"
              :isHorizontalLayout="isHorizontalLayout">
              <BaseInput
                v-if="data.defaultTraderSite"
                mode="view"
                v-model="data.defaultTraderSite.city"
                color="base"
                flat
                :disabled="true" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow
              mode="view"
              :name="$t('customer.traderSiteAddress')"
              :isHorizontalLayout="isHorizontalLayout">
              <BaseInput
                v-if="data.defaultTraderSite"
                mode="view"
                v-model="data.defaultTraderSite.address"
                color="base"
                flat
                :disabled="true" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow
              mode="view"
              :name="$t('customer.traderSiteZipCode')"
              :isHorizontalLayout="isHorizontalLayout">
              <BaseInput
                v-if="data.defaultTraderSite"
                mode="view"
                v-model="data.defaultTraderSite.zipCode"
                color="base"
                flat
                :disabled="true" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow
              mode="view"
              :name="$t('customer.traderSiteIsHQ')"
              :isHorizontalLayout="isHorizontalLayout">
              <BaseCheckbox
                v-if="data.defaultTraderSite"
                mode="view"
                v-model="data.defaultTraderSite.isHQ"
                color="base"
                flat
                :disabled="true" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow
              :name="$t('baseObject.isActive')"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout">
              <BaseTableCellStatus
                :item="data.defaultTraderSite?.isActive ? data.defaultTraderSite?.isActive : 0" />
            </BaseDetailsTableRow>
          </tbody>
        </table>
      </div>
    </template>
    <template #detailsTitle>
      <nav
        role="section-nav"
        class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
        <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
          <li
            v-for="tab in filteredDetailsTabs"
            :key="tab.name"
            @click="selectedDetailsTab = tab.name"
            class="py-1.5 cursor-pointer"
            :class="selectedDetailsTab === tab.name ? 'border-b-2 border-theme-500' : ''">
            <p>
              {{ tab.label }}
            </p>
          </li>
        </ul>
      </nav>
    </template>
    <template #detailsContent>
      <CustomerDetailsTableTraderSites
        v-if="selectedDetailsTab === 'traderSites'"
        :trader-sites="localTraderSites"
        :mode="mode"
        :trader-name="data?.trader?.name"
        @save-trader-site="onSaveTraderSite" />

      <CustomerDetailsTableRanges
        v-else-if="selectedDetailsTab === 'ranges' && enableRanges"
        :items="filteredAssignedRanges"
        :mode="mode"
        :trader-sites="localTraderSites"
        :customer-id="data?.id"
        @add="onAddRangeTradersiteCustomers"
        @delete="onDeleteRangeTradersiteCustomers"
        @update="onUpdateRangeTradersiteCustomers">
      </CustomerDetailsTableRanges>
    </template>
  </AppResizableContainer>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import BaseInput from '../common/BaseInput.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseSearchDropdown from '../common/BaseSearchDropdown.vue';
import { type Customer, type Representative, type TraderSite } from '@/types/Traders';
import { RecordMode, type SelectDropdownItem } from '@/types/Base';
import { getTraderSiteById, getTraderSitesWithConditions } from '@/services/api/traders';
import { getRangeCustomerTradersitesWithConditions } from '@/services/api/ranges';
import BaseCheckbox from '../common/BaseCheckbox.vue';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import AppResizableContainer from '../common/AppResizableContainer.vue';
import CustomerDetailsTableTraderSites from './CustomerDetailsTableTraderSites.vue';
import CustomerDetailsTableRanges from './CustomerDetailsTableRanges.vue';
import type { RangeCustomerTradersite } from '@/types/Ranges';
import { deepCopy } from '@/utils/data';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  resetState: boolean;
  enableRanges?: boolean;
}>();

const enableRanges = props.enableRanges !== undefined ? props.enableRanges : true;

const { t: $t } = useI18n();
const toastStore = useToastStore();
const data = defineModel<Customer | Representative>();
const localTraderSites = ref<TraderSite[]>([]);
const assignedRanges = defineModel<RangeCustomerTradersite[]>('assignedRanges');
const originalAssignedRanges = ref<RangeCustomerTradersite[]>([]);

const filteredAssignedRanges = computed(() => {
  if (!enableRanges) return [];
  return assignedRanges.value
    ? assignedRanges.value.filter((x) => x.recordMode !== RecordMode.Deleted)
    : [];
});

const filteredTraderSites = ref<SelectDropdownItem[]>([]);
const selectedTraderSite = ref<SelectDropdownItem | null>(null);
const traderSitesCount = ref(0);

async function updateSearch(searchInput: string, page: number) {
  if (!data.value) return;
  if (!data.value.traderId) {
    toastStore.warning({ text: 'You have to select a trader first.' });
    return;
  }
  const traderSites = await getTraderSites(searchInput, page);
  if (!traderSites) return;
  if (traderSites.count > 0 && traderSites.data) {
    if (page > 1 && filteredTraderSites.value)
      filteredTraderSites.value = filteredTraderSites.value.concat(
        traderSites.data.map((t) => ({
          value: t.id,
          name: getTraderSiteDropdownDisplayName(t),
        })) as SelectDropdownItem[]
      );
    else
      filteredTraderSites.value = traderSites.data.map((t) => ({
        value: t.id || '',
        name: getTraderSiteDropdownDisplayName(t),
      }));
  } else filteredTraderSites.value = [];
  traderSitesCount.value = traderSites.count;
}

async function handleTraderSiteSelection(item: SelectDropdownItem) {
  selectedTraderSite.value = item;

  if (data.value && data.value.defaultTraderSiteId !== item.value) {
    const defaultTraderSite = await getTraderSiteById(item.value as string);
    if (defaultTraderSite) {
      data.value.defaultTraderSiteId = item.value as string;
      data.value.defaultTraderSite = defaultTraderSite;
    }
  }
}

async function getTraderSites(searchInput: string = '', page: number = 1, itemsPerPage = 20) {
  if (!data.value) return;
  return await getTraderSitesWithConditions({
    itemsPerPage: itemsPerPage,
    sortColumn: 'name',
    sortAscending: true,
    page: page,
    traderId: data.value.traderId,
    query: searchInput,
  });
}

function initDropdowns() {
  if (!data.value) return;
  if (data.value.defaultTraderSite && data.value.defaultTraderSiteId) {
    selectedTraderSite.value = {
      value: data.value.defaultTraderSite.id,
      name: getTraderSiteDropdownDisplayName(data.value.defaultTraderSite),
    } as SelectDropdownItem;
    if (!filteredTraderSites.value.find((ts) => ts.value == selectedTraderSite.value?.value))
      filteredTraderSites.value = [...filteredTraderSites.value, selectedTraderSite.value];
  } else {
    if (data.value.traderId) updateSearch('', 1);
  }
}

async function loadAssignedRanges() {
  if (!data.value) return;
  const res = await getRangeCustomerTradersitesWithConditions({
    customerId: data.value.id,
  });
  if (res) {
    originalAssignedRanges.value = res.data;
    assignedRanges.value = deepCopy<RangeCustomerTradersite[]>(originalAssignedRanges.value);
  }
}

onMounted(async () => {
  if (!data.value) return;
  initDropdowns();
  await loadAssignedRanges();
  const sites = await getTraderSites('', 1, 0);
  if (sites?.data) {
    localTraderSites.value = sites.data;
  }
});

function getTraderSiteDropdownDisplayName(defaultTraderSite: Partial<TraderSite>) {
  return `${defaultTraderSite.name ?? ''} | ${defaultTraderSite.code ?? ''} | ${defaultTraderSite.city ?? ''} | ${defaultTraderSite.address ?? ''}`;
}

function onSaveTraderSite(site: TraderSite) {
  const index = localTraderSites.value.findIndex((existingItem) => existingItem.id === site.id);

  if (index !== -1) {
    if (
      localTraderSites.value[index].recordMode === RecordMode.Added &&
      site.recordMode === RecordMode.Deleted
    ) {
      localTraderSites.value.splice(index, 1);
    } else {
      localTraderSites.value[index] = site;
    }
  }

  if (site.recordMode === RecordMode.Added) {
    localTraderSites.value.unshift(site);
  }

  if (data.value) {
    data.value = {
      ...data.value,
      traderSites: localTraderSites.value,
    };
  }
}

watch(
  () => props.resetState,
  async (newValue) => {
    if (!newValue || !data.value) return;
    await loadAssignedRanges();
  }
);

const selectedDetailsTab = ref('traderSites');

const detailsTabs = ref([
  {
    name: 'traderSites',
    label: $t('customer.traderSites'),
  },
  {
    name: 'ranges',
    label: $t('customer.ranges'),
  },
]);

const filteredDetailsTabs = computed(() => {
  if (enableRanges) {
    return detailsTabs.value;
  } else {
    return detailsTabs.value.filter((tab) => tab.name !== 'ranges');
  }
});

async function onAddRangeTradersiteCustomers(item: RangeCustomerTradersite) {
  if (assignedRanges.value) assignedRanges.value.push(item);
}

function onDeleteRangeTradersiteCustomers(items: RangeCustomerTradersite[]) {
  items.forEach((item) => {
    if (!assignedRanges.value) return;
    const existingIndex = assignedRanges.value.findIndex((x) => x.id === item.id);
    if (existingIndex !== -1) {
      const existingItem = assignedRanges.value[existingIndex];
      if (existingItem.recordMode === RecordMode.Added) {
        assignedRanges.value.splice(existingIndex, 1);
      } else if (existingItem.recordMode === RecordMode.Modified) {
        existingItem.recordMode = RecordMode.Deleted;
      } else {
        existingItem.recordMode = RecordMode.Deleted;
      }
    } else {
      return;
    }
  });
}

function onUpdateRangeTradersiteCustomers(item: RangeCustomerTradersite) {
  if (!assignedRanges.value) return;
  const existingIndex = assignedRanges.value.findIndex((x) => x.id === item.id);
  if (existingIndex !== -1) assignedRanges.value[existingIndex] = item;
}
</script>
